import type { BrokerageAccountSummary, MarginHeadroom } from "../types";
import { money } from "../lib/format";
import { Skeleton } from "@/components/ui/skeleton";

function positive(value: number | null | undefined): number {
  return value != null && Number.isFinite(value) ? Math.max(0, value) : 0;
}

function share(value: number, total: number): number {
  return total > 0 ? Math.min(100, Math.max(0, (value / total) * 100)) : 0;
}

export function AccountRelationshipBars({
  summary,
  headroom,
  compact = false,
  showPortfolioDetails = true,
}: {
  summary: BrokerageAccountSummary;
  headroom?: MarginHeadroom | null;
  compact?: boolean;
  showPortfolioDetails?: boolean;
}) {
  const reference = positive(summary.account_reference_value ?? summary.total_equity);
  const allEquity = positive(summary.all_equity ?? summary.total_equity ?? reference);
  const marginable = Math.min(
    allEquity,
    positive(summary.marginable_equity ?? reference),
  );
  const otherEquity = Math.max(0, allEquity - marginable);
  const margin = positive(summary.margin_balance);
  const available = positive(summary.available_funds);
  const ltv = summary.ltv_percent ?? (reference > 0 ? (margin / reference) * 100 : null);
  const marginWidth = ltv == null ? 0 : Math.min(100, Math.max(0, ltv));
  const buyingPowerWidth = share(available, allEquity || reference);
  const currentLtv = positive(headroom?.current_ltv_percent ?? summary.ltv_percent);
  const permitted = positive(headroom?.permitted_margin_percent);
  const riskScale = Math.max(currentLtv, permitted, 1);

  return (
    <div
      className={compact ? "space-y-2.5" : "space-y-3"}
      data-testid="account-relationship-bars"
    >
      {showPortfolioDetails && summary.source === "plaid" && allEquity > 0 && (
        <div data-testid="equity-composition-bar">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px]">
            <span className="font-medium">Equity composition</span>
            <span className="tabular-nums text-muted-foreground">{money(allEquity)}</span>
          </div>
          <div className="flex h-2 overflow-hidden rounded-full bg-muted">
            <span
              className="bg-primary"
              style={{ width: `${share(marginable, allEquity)}%` }}
              title={`Marginable equity ${money(marginable)}`}
            />
            <span
              className="bg-primary/30"
              style={{ width: `${share(otherEquity, allEquity)}%` }}
              title={`Non-marginable equity ${money(otherEquity)}`}
            />
          </div>
        </div>
      )}

      {reference > 0 && (
        <div data-testid="margin-utilization-bar">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px]">
            <span className="font-medium">Margin relative to LTV base</span>
            <span className="tabular-nums text-muted-foreground">
              {ltv == null ? "—" : `${ltv.toFixed(2)}%`} of {money(reference)}
            </span>
          </div>
          <div className="flex h-2 overflow-hidden rounded-full bg-muted">
            <span
              className={ltv != null && ltv > 100 ? "bg-destructive" : "bg-warning"}
              style={{ width: `${marginWidth}%` }}
              title={`Margin balance ${money(margin)}`}
            />
            <span className="flex-1 bg-muted" />
          </div>
        </div>
      )}

      {showPortfolioDetails &&
        summary.available_funds != null &&
        (allEquity > 0 || reference > 0) && (
        <div data-testid="buying-power-bar">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px]">
            <span className="font-medium">Available buying power</span>
            <span className="tabular-nums text-muted-foreground">
              {money(summary.available_funds)}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <span
              className="block h-full min-w-px bg-success"
              style={{ width: `${buyingPowerWidth}%` }}
              title={`${buyingPowerWidth.toFixed(2)}% of all equity`}
            />
          </div>
        </div>
        )}

      {headroom && headroom.permitted_margin_percent != null && (
        <div data-testid="margin-headroom-bar">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px]">
            <span className="font-medium">Current LTV vs permitted limit</span>
            <span className="tabular-nums text-muted-foreground">
              {currentLtv.toFixed(2)}% / {permitted.toFixed(2)}%
            </span>
          </div>
          <div className="relative h-2 overflow-hidden rounded-full bg-muted">
            <span
              className={`block h-full ${headroom.over_limit ? "bg-destructive" : "bg-success"}`}
              style={{ width: `${share(currentLtv, riskScale)}%` }}
            />
            <span
              className="absolute inset-y-0 w-0.5 bg-foreground"
              style={{ left: `calc(${share(permitted, riskScale)}% - 1px)` }}
              title={`Permitted limit ${permitted.toFixed(2)}%`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function AccountRelationshipBarsSkeleton({
  rows = 3,
  compact = false,
}: {
  rows?: number;
  compact?: boolean;
}) {
  return (
    <div
      className={compact ? "space-y-2.5" : "space-y-3"}
      data-testid="account-relationship-bars-skeleton"
      aria-busy="true"
    >
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index}>
          <div className="mb-1 flex justify-between gap-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}
